import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authManager } from '../../services/authManager';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredCapabilities?: string[];
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requiredCapabilities,
  redirectTo = '/workspace'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasRoleAccess, setHasRoleAccess] = useState(true);
  const [hasCapabilityAccess, setHasCapabilityAccess] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = authManager.isAuthenticated();

        if (authenticated) {
          const token = authManager.getAccessToken();

          if (token) {
            setIsAuthenticated(true);
            const user = authManager.getUser();
            const userRole = user?.role || '';
            const userRoles = user?.roles || [];
            const normalizedRole = String(userRole || '').toLowerCase().trim();
            const normalizedRoles = userRoles.map((r: string) => String(r || '').toLowerCase().trim());

            const hasRoleAdmin1 = normalizedRole === 'admin';
            const hasRoleAdmin2 = normalizedRoles.includes('admin');
            const hasRoleAdmin3 = authManager.hasRole('admin');
            const isAdmin = hasRoleAdmin1 || hasRoleAdmin2 || hasRoleAdmin3;

            const roleAllowed = !allowedRoles || allowedRoles.length === 0
              ? true
              : isAdmin || authManager.hasAnyRole(allowedRoles);
            const capabilityAllowed = !requiredCapabilities || requiredCapabilities.length === 0
              ? true
              : isAdmin || authManager.hasAllCapabilities(requiredCapabilities);

            setHasRoleAccess(roleAllowed);
            setHasCapabilityAccess(capabilityAllowed);
          } else {
            setIsAuthenticated(false);
            setHasRoleAccess(false);
            setHasCapabilityAccess(false);
          }
        } else {
          setIsAuthenticated(false);
          setHasRoleAccess(false);
          setHasCapabilityAccess(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setHasRoleAccess(false);
        setHasCapabilityAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname, allowedRoles, requiredCapabilities]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Rediriger vers la page de connexion avec l'URL de retour
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasRoleAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!hasCapabilityAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
